import { For, Show, createEffect, createSignal, onCleanup, onMount } from "solid-js";
import Map from "../components/Map";
import type { MapPointHighlight } from "../components/mapTypes";
import StatsPanel from "../components/StatsPanel";
import { formatTripDate } from "../lib/date";
import { createAppStore } from "../store";
import type { SettingKey } from "../types";

export default function Home() {
  const store = createAppStore();
  const [expanded, setExpanded] = createSignal(false);
  const [showModal, setShowModal] = createSignal(false);
  const [cellSource, setCellSource] = createSignal(-2);
  const [healthSource, setHealthSource] = createSignal("preferred");
  const [highlightedPoint, setHighlightedPoint] = createSignal<MapPointHighlight>();
  let mainContainer!: HTMLDivElement;
  let selectedTrip = store.state.activeTripId;

  createEffect(() => {
    mainContainer.toggleAttribute("inert", store.state.isBlockingLoad);
    if (store.state.activeTripId !== selectedTrip) {
      selectedTrip = store.state.activeTripId;
      setCellSource(-2);
      setHealthSource("preferred");
      setHighlightedPoint(undefined);
    }
    const sources = store.state.cellOverlay.sources;
    if (sources.length && (cellSource() === -2 || cellSource() >= sources.length)) {
      const active = sources.findIndex((source) => source.activeData);
      setCellSource(active >= 0 ? active : 0);
    }
  });

  onMount(() => void store.loadTrips());
  onCleanup(store.dispose);

  const setting = (key: SettingKey, label: string, disabled = false) => (
    <label class="form-switch">
      <input
        type="checkbox"
        checked={store.state.settings[key]}
        disabled={disabled}
        onChange={() => store.toggleSetting(key)}
      />
      <i class="form-icon" />
      {label}
    </label>
  );

  return (
    <div class="app-root" aria-busy={store.state.isBlockingLoad}>
      <div class="main-container" ref={mainContainer}>
        <aside class="sidebar" classList={{ expanded: expanded() }}>
          <button
            class="expand-button float-right"
            aria-label={expanded() ? "Collapse trips" : "Expand trips"}
            onClick={() => setExpanded((value) => !value)}
          >
            <i class="fa" classList={{ "fa-chevron-left": expanded(), "fa-chevron-right": !expanded() }} />
          </button>
          <div class="event-list">
            <ul>
              <For each={store.state.trips}>
                {(event) => (
                  <li
                    onClick={() => void store.selectTrip(event.id)}
                    class="event"
                    classList={{ active: store.state.activeTripId === event.id }}
                  >
                    <i class="fa fa-person-hiking" />
                    <div class="event-data-group">
                      <div class="event-title">{event.title}</div>
                      <div class="text-tiny">{formatTripDate(event.start)}</div>
                    </div>
                  </li>
                )}
              </For>
            </ul>
          </div>
        </aside>

        <div class="visual-column">
          <div class="map-shell">
            <Map
              trip={store.state.trip}
              cells={store.state.cellOverlay}
              dataRevision={store.state.tripRevision}
              showPoints={store.state.settings.showPoints}
              showCamps={store.state.settings.showCamps}
              showJoyScore={store.state.settings.showJoyScore}
              showTowers={store.state.settings.showTowers}
              showLines={store.state.settings.showLines}
              showCellCoverage={store.state.settings.showCellCoverage}
              selectedCellSource={cellSource()}
              health={store.state.health}
              selectedHealthSource={healthSource()}
              highlightedPoint={highlightedPoint()}
            />
            <Show when={store.state.settings.showPoints}>
              <div class="point-coverage-legend" aria-label="Path point cellular coverage legend">
                <span>
                  <i class="point-coverage-legend__dot is-red" />
                  No coverage
                </span>
                <span>
                  <i class="point-coverage-legend__dot is-yellow" />
                  Tower not found
                </span>
                <span>
                  <i class="point-coverage-legend__dot is-green" />
                  Tower found
                </span>
              </div>
            </Show>
          </div>
          <Show when={store.state.settings.showStats}>
            <StatsPanel
              tripId={store.state.activeTripId}
              trip={store.state.trip}
              stats={store.state.stats}
              health={store.state.health}
              loading={store.state.statsStatus === "loading"}
              healthSource={healthSource()}
              onHealthSourceChange={setHealthSource}
              onMapHighlightChange={setHighlightedPoint}
            />
          </Show>
        </div>

        <Show when={store.state.cellOverlay.sources.length > 0}>
          <label class="cell-source-control">
            Cellular source
            <select
              value={cellSource()}
              onChange={(event) => setCellSource(Number(event.currentTarget.value))}
            >
              <option value={-1}>All SIMs</option>
              <For each={store.state.cellOverlay.sources}>
                {(source, index) => (
                  <option value={index()}>
                    {source.carrierName || `SIM ${source.slotIndex + 1}`}
                    {source.activeData ? " · data" : ""}
                  </option>
                )}
              </For>
            </select>
          </label>
        </Show>

        <button class="settings-button" aria-label="Open settings" onClick={() => setShowModal(true)}>
          <i class="fa fa-cog" />
        </button>
        <div class="modal modal-sm" classList={{ active: showModal() }}>
          <button class="modal-overlay" aria-label="Close settings" onClick={() => setShowModal(false)} />
          <div class="modal-container" role="dialog" aria-modal="true" aria-label="Settings">
            <div class="modal-header">
              <button
                class="btn btn-clear float-right"
                aria-label="Close settings"
                onClick={() => setShowModal(false)}
              />
              <div class="modal-title h5">Settings</div>
            </div>
            <div class="modal-body">
              <div class="content">
                <form onSubmit={(event) => event.preventDefault()}>
                  <fieldset>
                    <legend>Trip dashboard</legend>
                    <div class="form-group">{setting("showStats", "Show trip statistics")}</div>
                  </fieldset>
                  <fieldset>
                    <legend>Geeky stuff</legend>
                    <div class="form-group">
                      {setting("showPoints", "Show points on path")}
                      {setting("showCamps", "Show camping locations")}
                      {setting("showTowers", "Show cell towers")}
                      {setting("showLines", "Show lines between points and towers")}
                      {setting("showCellCoverage", "Show cellular coverage and handoffs")}
                    </div>
                  </fieldset>
                  <fieldset>
                    <legend>Rytis section</legend>
                    <div class="form-group">{setting("showJoyScore", "Show joy and happiness")}</div>
                  </fieldset>
                </form>
              </div>
            </div>
          </div>
        </div>
        <Show when={store.state.error}>
          {(error) => (
            <div class="load-error" role="alert">
              <span>{error()}</span>
              <button
                type="button"
                class="btn btn-clear"
                aria-label="Dismiss error"
                onClick={store.dismissError}
              />
            </div>
          )}
        </Show>
        <Show when={store.state.liveStatus === "reconnecting"}>
          <div class="live-status" role="status">
            Live updates reconnecting…
          </div>
        </Show>
        <Show when={store.state.cellStatus === "loading"}>
          <div class="live-status" role="status">
            Loading cell data…
          </div>
        </Show>
      </div>

      <Show when={store.state.isBlockingLoad}>
        <div class="blocking-loader" role="status" aria-live="polite" aria-label="Loading hike">
          <div class="blocking-loader__spinner" aria-hidden="true" />
          <div>Loading hike…</div>
        </div>
      </Show>
    </div>
  );
}
